import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { SingleUnit, EquipmentCategory, VendorList } from '../../types/schema';
import { useEquipmentContext } from '../../contexts/EquipmentContext';
import { useUserContext } from '../../contexts/UserContext';
import { useCostTracking } from '../../contexts/CostTrackingContext';
import type { ExpenseCategoryEntry } from '../../types/costTrackingTypes';
import { calculateTotalExpense, parseLaborEntries, expenseCategoriesForEquipment, laborHoursLabel, dateInputToIso, expenseDateInputValue } from '../../utils/costTrackingUtils';
import {
    PM_INTERNAL_LABOR_CATEGORY,
    PM_INTERNAL_SERVICE_CATEGORY,
    pmServiceLevelFieldsToSave,
    summarizePmLaborEntries,
} from '../../utils/pmCostTracking';
import { fetchLaborCategoriesByCompany, getCurrentLaborRate } from '../../graphql/pmQueries';
import {
    createExpenseRecord,
    updateExpenseRecord,
    createExpenseCategory as createExpenseCategoryMutation,
    createUnitExpenseBudget as createUnitExpenseBudgetMutation,
} from '../../graphql/costTrackingQueries';
import { VendorModal } from '../company-resources/VendorModal';
import { authClient } from '../../utils/amplifyClient';
import { listVendorListByCompanyIdAndUpdatedAt } from '../../graphql/queries';
import { uploadData } from 'aws-amplify/storage';
import { v4 as uuidv4 } from 'uuid';
import {
    BackButton,
    Btn,
    BtnIconOnly,
    Card,
    DateField,
    IconClose,
    IconPlus,
    IconTrash,
    IconUpload,
    InputField,
    SearchSuggest,
    Select,
} from '../ui';
import '../../css/RecordExpenseForm.css';



// ---------- Types ----------

interface RecordExpenseFormProps {
    expenseId?: string;
    onBack: () => void;
    onSave: () => void;
}

interface LaborLineItem {
    id: string;
    categoryId: string;
    categoryName: string;
    hoursST: number;
    hoursOT: number;
    rateST: number;
    rateOT: number;
}

interface LaborRateOption {
    id: string;
    name: string;
    rateST: number;
    rateOT: number;
}

const blankLaborLine = (): LaborLineItem => ({
    id: uuidv4(),
    categoryId: '',
    categoryName: '',
    hoursST: 0,
    hoursOT: 0,
    rateST: 0,
    rateOT: 0,
});

interface CategoryLineItem {
    id: string; // local key
    categoryId: string;
    categoryName: string;
    startingBalance: number;
    cost: number;
    isNew: boolean; // true when user typed a new name
    isEditing: boolean;
}

interface AttachmentFile {
    id: string;
    file?: File;
    name: string;
    s3Path?: string; // set for already-uploaded files in edit mode
}

// ---------- Helpers ----------

const todayISO = () => {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${now.getFullYear()}-${month}-${day}`;
};

const formatCurrency = (val: number) =>
    val.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const sortByName = (a: { name?: string | null }, b: { name?: string | null }) =>
    (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' });

const ACCEPTED_FILE_TYPES = '.pdf,.png,.jpg,.jpeg,.gif,.doc,.docx,.xls,.xlsx,.csv';

// ===================================================================
// RecordExpenseForm — Tasks 6.1 through 6.8
// ===================================================================

export const RecordExpenseForm = ({ expenseId, onBack, onSave }: RecordExpenseFormProps) => {
    const { companyEquipmentCategories, companyUnits } = useEquipmentContext();
    const { companyUser, companyOwner } = useUserContext();
    const {
        expenseCategories,
        unitExpenseBudgets,
        expenseRecords,
        refreshExpenseData,
        getStartingBalanceForUnitCategory,
    } = useCostTracking();
    const companyId = (companyUser?.companyId as string) || companyOwner?.companyId || '';
    const isEditMode = Boolean(expenseId);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const hydratedExpenseIdRef = useRef<string | null>(null);

    // ---- Vendor data (fetched locally like PMVendorListTab) ----
    const [vendors, setVendors] = useState<VendorList[]>([]);

    useEffect(() => {
        if (!companyId) return;
        (async () => {
            try {
                const result = await authClient.graphql({
                    query: listVendorListByCompanyIdAndUpdatedAt,
                    variables: { companyId, limit: 1000 },
                });
                const items = (result.data.listVendorListByCompanyIdAndUpdatedAt.items || []).filter(Boolean) as VendorList[];
                setVendors(items);
            } catch (err) {
                console.error('Failed to fetch vendors:', err);
                Sentry.captureException(err);
            }
        })();
    }, [companyId]);


    // ---- Vendor create modal state ----
    const [showVendorModal, setShowVendorModal] = useState(false);
    const [vendorDraftName, setVendorDraftName] = useState('');

    // ---- Equipment selection state ----
    const [selectedCategoryId, setSelectedCategoryId] = useState('');
    const [selectedUnitId, setSelectedUnitId] = useState('');

    // ---- Service Details state (Task 6.2) ----
    const [dateOfInput] = useState(todayISO());
    const [dateOfService, setDateOfService] = useState(todayISO());
    const [vendorId, setVendorId] = useState('');
    const [workDescription, setWorkDescription] = useState('');
    const [invoiceNumber, setInvoiceNumber] = useState('');

    // ---- PM level fields preserved for enrolled units (posted from PM Management) ----
    const [recordedPmLevelId, setRecordedPmLevelId] = useState('');
    const [recordedPmLevelName, setRecordedPmLevelName] = useState('');

    // ---- Expense Categories state (Task 6.4) ----
    const [categoryLines, setCategoryLines] = useState<CategoryLineItem[]>([]);

    // ---- Labor: one row per Resources labor level ----
    const [laborLines, setLaborLines] = useState<LaborLineItem[]>(() => [blankLaborLine()]);
    const [laborOptions, setLaborOptions] = useState<LaborRateOption[]>([]);

    // ---- Attachments state (Task 6.6) ----
    const [attachments, setAttachments] = useState<AttachmentFile[]>([]);

    // ---- Save state ----
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState('');

    // ---- Derived data ----

    const categoryOptions = useMemo(
        () =>
            [...companyEquipmentCategories]
                .sort(sortByName)
                .map((cat: EquipmentCategory) => ({
                    value: cat.id,
                    label: cat.name,
                })),
        [companyEquipmentCategories],
    );

    const filteredUnits = useMemo(() => {
        const units = selectedCategoryId
            ? companyUnits.filter((u: SingleUnit) => u.categoryId === selectedCategoryId)
            : companyUnits;
        return [...units].sort(sortByName);
    }, [companyUnits, selectedCategoryId]);

    const currentUnit = useMemo(
        () => companyUnits.find((u: SingleUnit) => u.id === selectedUnitId) ?? null,
        [companyUnits, selectedUnitId],
    );

    const unitDescription = currentUnit?.description || '';

    const pmCostPostsOnSubmit = Boolean(currentUnit?.pmModuleEnrollment);
    const equipmentCategoryIdForCosts = selectedCategoryId || currentUnit?.categoryId || '';
    const scopedExpenseCategories = useMemo(
        () => [...expenseCategoriesForEquipment(expenseCategories, equipmentCategoryIdForCosts)].sort(sortByName),
        [expenseCategories, equipmentCategoryIdForCosts],
    );

    const vendorOptions = useMemo(
        () => [...vendors].sort(sortByName).map((vendor) => ({ value: vendor.id, label: vendor.name })),
        [vendors],
    );

    useEffect(() => {
        if (!companyId) return;
        let isCancelled = false;
        (async () => {
            try {
                const cats = await fetchLaborCategoriesByCompany(companyId);
                const active = (cats || []).filter((category) => category?.isActive && category.id && category.name);
                const options: LaborRateOption[] = [];
                for (const category of active) {
                    let rateST = 0;
                    let rateOT = 0;
                    try {
                        const rate = await getCurrentLaborRate(category.id);
                        if (isCancelled) return;
                        rateST = rate?.standardRate || 0;
                        rateOT = rate?.overtimeRate || 0;
                    } catch {
                        /* a level with no dollar rate still records hours */
                    }
                    options.push({ id: category.id, name: category.name, rateST, rateOT });
                }
                if (!isCancelled) setLaborOptions(options);
            } catch (err) {
                if (!isCancelled) {
                    console.error('Failed to fetch labor rates:', err);
                    Sentry.captureException(err);
                }
            }
        })();
        return () => {
            isCancelled = true;
        };
    }, [companyId]);

    // ---- Total Expense Calculation (Task 6.7) ----
    const laborSummary = summarizePmLaborEntries(
        laborLines.map((line) => ({
            categoryId: line.categoryId || 'hours',
            categoryName: line.categoryName,
            standardRate: line.rateST,
            overtimeRate: line.rateOT,
            hoursST: line.hoursST,
            hoursOT: line.hoursOT,
        })),
    );
    const laborCost = laborSummary.totalLaborCost;
    const laborSummaryText = laborCost > 0 ? formatCurrency(laborCost) : (laborHoursLabel(laborSummary.laborHoursST, laborSummary.laborHoursOT) || formatCurrency(0));
    const totalExpense = calculateTotalExpense({
        pmExpense: 0,
        categoryEntries: categoryLines.map((l) => ({ cost: l.cost || 0 })),
        laborHoursST: laborSummary.laborHoursST,
        laborHoursOT: laborSummary.laborHoursOT,
        laborRateST: laborSummary.laborRateST,
        laborRateOT: laborSummary.laborRateOT,
    });


    // ---- Edit mode: load existing record once ----
    useEffect(() => {
        if (!expenseId) {
            hydratedExpenseIdRef.current = null;
            return;
        }
        if (hydratedExpenseIdRef.current === expenseId) return;
        const record = expenseRecords.find((r) => r.id === expenseId);
        if (!record) return;
        hydratedExpenseIdRef.current = expenseId;

        // Equipment — backfill category from the unit when the PM expense omitted it
        const unitForRecord = companyUnits.find((u: SingleUnit) => u.id === record.unitId);
        setSelectedCategoryId(record.equipmentCategoryId || unitForRecord?.categoryId || '');
        setSelectedUnitId(record.unitId);

        // Service details
        setDateOfService(expenseDateInputValue(record.dateOfService) || todayISO());
        setVendorId(record.vendorId || '');
        setWorkDescription(record.workDescription || '');
        setInvoiceNumber(record.invoiceNumber || '');

        // Preserve PM level labels from records posted via PM Management
        setRecordedPmLevelId(record.pmServiceLevelId || '');
        setRecordedPmLevelName(record.pmServiceLevelName || '');

        // Categories
        try {
            const parsed: ExpenseCategoryEntry[] = JSON.parse(record.expenseCategories || '[]');
            setCategoryLines(
                parsed.map((e) => ({
                    id: uuidv4(),
                    categoryId: e.categoryId,
                    categoryName: e.categoryName,
                    startingBalance: getStartingBalanceForUnitCategory(record.unitId, e.categoryId) ?? 0,
                    cost: e.cost,
                    isNew: false,
                    isEditing: false,
                })),
            );
        } catch {
            setCategoryLines([]);
        }

        // Labor
        const storedLabor = parseLaborEntries(record.laborEntries);
        if (storedLabor.length > 0) {
            setLaborLines(storedLabor.map((line) => ({ ...line, id: uuidv4() })));
        } else if ((record.laborHoursST || 0) > 0 || (record.laborHoursOT || 0) > 0) {
            setLaborLines([{
                id: uuidv4(),
                categoryId: record.laborRateScheduleId || '',
                categoryName: record.laborRateScheduleName || '',
                hoursST: record.laborHoursST || 0,
                hoursOT: record.laborHoursOT || 0,
                rateST: record.laborRateST || 0,
                rateOT: record.laborRateOT || 0,
            }]);
        } else {
            setLaborLines([blankLaborLine()]);
        }

        // Attachments (existing S3 paths)
        if (record.attachments?.length) {
            setAttachments(
                record.attachments.filter(Boolean).map((path) => ({
                    id: uuidv4(),
                    name: path!.split('/').pop() || path!,
                    s3Path: path!,
                })),
            );
        }
    }, [expenseId, expenseRecords, companyUnits, getStartingBalanceForUnitCategory]);

    // When a PM expense omitted Equipment Category, fill it from the selected unit once units load.
    useEffect(() => {
        if (!selectedUnitId || selectedCategoryId) return;
        const unitForRecord = companyUnits.find((u: SingleUnit) => u.id === selectedUnitId);
        if (unitForRecord?.categoryId) {
            setSelectedCategoryId(unitForRecord.categoryId);
        }
    }, [selectedUnitId, selectedCategoryId, companyUnits]);

    const categorySeedKeyRef = useRef('');
    useEffect(() => {
        if (!selectedUnitId) {
            if (!isEditMode) {
                categorySeedKeyRef.current = '';
                setCategoryLines([]);
            }
            return;
        }
        if (isEditMode && hydratedExpenseIdRef.current !== expenseId) return;
        if (scopedExpenseCategories.length === 0) return;
        const seedKey = `${selectedUnitId}|${scopedExpenseCategories.map((category) => category.id).join(',')}`;
        if (categorySeedKeyRef.current === seedKey) return;
        categorySeedKeyRef.current = seedKey;

        setCategoryLines((previous) => {
            const previousById = new Map(
                previous.filter((line) => line.categoryId).map((line) => [line.categoryId, line]),
            );
            const rows = scopedExpenseCategories.map((category) => {
                const existing = previousById.get(category.id);
                const balance = selectedUnitId
                    ? getStartingBalanceForUnitCategory(selectedUnitId, category.id) ?? category.startingBalance ?? 0
                    : category.startingBalance ?? 0;
                return {
                    id: existing?.id || uuidv4(),
                    categoryId: category.id,
                    categoryName: category.name,
                    startingBalance: existing?.startingBalance ?? balance,
                    cost: existing?.cost ?? 0,
                    isNew: false,
                    isEditing: false,
                };
            });
            // Keep PM-posted Internal PM Service / Internal Labor rows (empty categoryId,
            // isNew false) and user-added unnamed drafts so edit/save cannot wipe them.
            const extras = previous.filter((line) => {
                if (line.categoryId) return false;
                const name = line.categoryName.trim();
                if (!name) return false;
                if (line.isNew) return true;
                return (
                    name === PM_INTERNAL_SERVICE_CATEGORY
                    || name === PM_INTERNAL_LABOR_CATEGORY
                );
            });
            return [...rows, ...extras];
        });
    }, [
        selectedUnitId,
        scopedExpenseCategories,
        isEditMode,
        expenseId,
        expenseRecords,
        getStartingBalanceForUnitCategory,
    ]);

    // Reset PM level labels when switching units on a new expense.
    useEffect(() => {
        if (!isEditMode) {
            setRecordedPmLevelId('');
            setRecordedPmLevelName('');
            setLaborLines([blankLaborLine()]);
            if (currentUnit?.categoryId) {
                setSelectedCategoryId(currentUnit.categoryId);
            }
        }
    }, [selectedUnitId, isEditMode, currentUnit?.categoryId]);

    const handleVendorModalSave = async () => {
        // Refresh vendor list and auto-select the newest vendor
        try {
            const result = await authClient.graphql({
                query: listVendorListByCompanyIdAndUpdatedAt,
                variables: { companyId, limit: 1000 },
            });
            const items = (result.data.listVendorListByCompanyIdAndUpdatedAt.items || []).filter(Boolean) as VendorList[];
            setVendors([...items].sort(sortByName));
            // Auto-select the most recently created vendor (last by updatedAt)
            const sorted = [...items].sort(
                (a, b) => new Date(b.updatedAt ?? 0).getTime() - new Date(a.updatedAt ?? 0).getTime(),
            );
            if (sorted.length > 0) {
                setVendorId(sorted[0].id);
            }
        } catch (err) {
            console.error('Failed to refresh vendors after create:', err);
            Sentry.captureException(err);
        }
        setShowVendorModal(false);
    };

    // ---- Category line handlers (Task 6.4) ----

    const handleRemoveCategoryLine = (lineId: string) => {
        setCategoryLines((prev) => prev.filter((l) => l.id !== lineId));
    };

    const handleCategoryBalanceChange = (lineId: string, balance: number) => {
        setCategoryLines((prev) =>
            prev.map((l) => (l.id === lineId ? { ...l, startingBalance: balance } : l)),
        );
    };

    const handleCategoryCostChange = (lineId: string, cost: number) => {
        setCategoryLines((prev) =>
            prev.map((l) => (l.id === lineId ? { ...l, cost } : l)),
        );
    };

    // ---- Attachment handlers (Task 6.6) ----

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files) return;
        const newAttachments: AttachmentFile[] = Array.from(files).map((f) => ({
            id: uuidv4(),
            file: f,
            name: f.name,
        }));
        setAttachments((prev) => [...prev, ...newAttachments]);
        // Reset input so same file can be re-selected
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleRemoveAttachment = (attId: string) => {
        setAttachments((prev) => prev.filter((a) => a.id !== attId));
    };

    // ---- Save logic (Task 6.8) ----

    const resetForm = () => {
        setSelectedCategoryId('');
        setSelectedUnitId('');
        setDateOfService(todayISO());
        setVendorId('');
        setWorkDescription('');
        setInvoiceNumber('');
        setRecordedPmLevelId('');
        setRecordedPmLevelName('');
        setCategoryLines([]);
        setLaborLines([blankLaborLine()]);
        setAttachments([]);
        setSaveError('');
    };

    const handleSave = useCallback(
        async (andAddAnother: boolean) => {
            setSaveError('');

            // Validation
            if (!selectedUnitId) {
                setSaveError('Please select an equipment unit.');
                return;
            }
            if (!dateOfService) {
                setSaveError('Please select a date of service.');
                return;
            }

            setIsSaving(true);
            try {
                // 1. Create new ExpenseCategory records for any new categories
                const resolvedCategories: ExpenseCategoryEntry[] = [];
                for (const line of categoryLines) {
                    let catId = line.categoryId;
                    const catName = line.categoryName.trim();
                    if (!catName) continue;
                    if ((line.cost || 0) <= 0 && !(line.isNew && line.startingBalance > 0)) continue;

                    if (line.isNew && !catId) {
                        // Create new ExpenseCategory
                        const newCat = await createExpenseCategoryMutation({
                            companyId,
                            name: catName,
                            startingBalance: line.startingBalance || 0,
                            equipmentCategoryId: equipmentCategoryIdForCosts || null,
                        });
                        if (!newCat) throw new Error('Failed to create expense category');
                        catId = newCat.id;

                        // Create UnitExpenseBudget for this unit-category pair
                        if (selectedUnitId && line.startingBalance > 0) {
                            await createUnitExpenseBudgetMutation({
                                companyId,
                                unitId: selectedUnitId,
                                expenseCategoryId: catId,
                                startingBalance: line.startingBalance,
                            });
                        }
                    } else if (catId && selectedUnitId) {
                        // Check if UnitExpenseBudget exists for this pair; if not, create one
                        const existingBudget = unitExpenseBudgets.find(
                            (b) => b.unitId === selectedUnitId && b.expenseCategoryId === catId,
                        );
                        if (!existingBudget && line.startingBalance > 0) {
                            await createUnitExpenseBudgetMutation({
                                companyId,
                                unitId: selectedUnitId,
                                expenseCategoryId: catId,
                                startingBalance: line.startingBalance,
                            });
                        }
                    }

                    resolvedCategories.push({
                        categoryId: catId,
                        categoryName: catName,
                        cost: line.cost || 0,
                    });
                }

                // 2. Upload new attachments to S3
                const recordId = expenseId || uuidv4();
                const allAttachmentPaths: string[] = [];

                for (const att of attachments) {
                    if (att.s3Path) {
                        // Already uploaded (edit mode)
                        allAttachmentPaths.push(att.s3Path);
                    } else if (att.file) {
                        const s3Path = `public/costTracking/${companyId}/${recordId}/${att.file.name}`;
                        try {
                            await uploadData({
                                path: s3Path,
                                data: att.file,
                            });
                            allAttachmentPaths.push(s3Path);
                        } catch (uploadErr) {
                            console.error('Attachment upload failed:', uploadErr);
                            Sentry.captureException(uploadErr);
                            // Continue — don't block save
                        }
                    }
                }

                // 3. Build vendor name
                const selectedVendor = vendors.find((v) => v.id === vendorId);
                const vendorName = selectedVendor?.name || '';

                // 4. Build the record input
                const computedTotal = calculateTotalExpense({
                    pmExpense: 0,
                    categoryEntries: resolvedCategories.map((c) => ({ cost: c.cost })),
                    laborHoursST: laborSummary.laborHoursST,
                    laborHoursOT: laborSummary.laborHoursOT,
                    laborRateST: laborSummary.laborRateST,
                    laborRateOT: laborSummary.laborRateOT,
                });
                const persistedLabor = laborSummary.lines.map((line) => ({
                    ...line,
                    categoryId: line.categoryId === 'hours' ? '' : line.categoryId,
                }));

                // Cost Tracking no longer captures PM level/cost; preserve labels only for
                // enrolled units whose expense was posted from PM Management.
                const savedPmLevel = pmServiceLevelFieldsToSave({
                    postsOnSubmit: pmCostPostsOnSubmit,
                    selectedLevelId: '',
                    selectedLevelName: '',
                    storedLevelId: recordedPmLevelId,
                    storedLevelName: recordedPmLevelName,
                });
                const recordInput = {
                    companyId,
                    unitId: selectedUnitId,
                    equipmentCategoryId: selectedCategoryId || null,
                    dateOfInput: dateInputToIso(dateOfInput),
                    dateOfService: dateInputToIso(dateOfService),
                    vendorId: vendorId || null,
                    vendorName: vendorName || null,
                    workDescription: workDescription || null,
                    invoiceNumber: invoiceNumber || null,
                    pmServiceLevelId: savedPmLevel.pmServiceLevelId,
                    pmServiceLevelName: savedPmLevel.pmServiceLevelName,
                    pmExpense: 0,
                    pmPartsSnapshot: '[]',
                    expenseCategories: JSON.stringify(resolvedCategories),
                    laborEntries: JSON.stringify(persistedLabor),
                    laborRateScheduleId: persistedLabor.length === 1 ? persistedLabor[0].categoryId || null : null,
                    laborRateScheduleName: laborSummary.laborRateScheduleName || null,
                    laborHoursST: laborSummary.laborHoursST,
                    laborHoursOT: laborSummary.laborHoursOT,
                    laborRateST: laborSummary.laborRateST,
                    laborRateOT: laborSummary.laborRateOT,
                    totalLaborCost: laborSummary.totalLaborCost,
                    attachments: allAttachmentPaths.length > 0 ? allAttachmentPaths : null,
                    totalExpense: computedTotal,
                };

                if (isEditMode && expenseId) {
                    await updateExpenseRecord({ id: expenseId, ...recordInput });
                } else {
                    await createExpenseRecord({ id: recordId, ...recordInput });
                }

                await refreshExpenseData();

                if (andAddAnother) {
                    resetForm();
                } else {
                    onSave();
                }
            } catch (err) {
                console.error('Save failed:', err);
                Sentry.captureException(err);
                setSaveError('Failed to save expense record. Please try again.');
            } finally {
                setIsSaving(false);
            }
        },
        [
            selectedUnitId, dateOfService, categoryLines, companyId, expenseId,
            attachments, vendors, vendorId, laborSummary,
            pmCostPostsOnSubmit, recordedPmLevelId, recordedPmLevelName,
            selectedCategoryId, equipmentCategoryIdForCosts, dateOfInput, workDescription,
            invoiceNumber, isEditMode, unitExpenseBudgets, refreshExpenseData, onSave,
            getStartingBalanceForUnitCategory, expenseCategories,
        ],
    );


    // ===================================================================
    // RENDER
    // ===================================================================

    return (
        <div className="ct-form-container">
            <BackButton onClick={onBack} buttonText="Back to Dashboard" />

            <h2 className="ct-form-title">
                {isEditMode ? 'Edit Expense Record' : 'Record New Expense / Cost'}
            </h2>

            {saveError && <div className="ct-form-error-banner">{saveError}</div>}

            <div className="ct-form-section ct-equip-header">
                <div className="ct-form-field">
                    <label htmlFor="ct-equip-category">Equipment Category</label>
                    <Select
                        inputId="ct-equip-category"
                        className="ct-form-select"
                        ariaLabel="Equipment Category"
                        placeholder="— Select Category —"
                        value={selectedCategoryId}
                        onChange={(value) => {
                            setSelectedCategoryId(value);
                            setSelectedUnitId('');
                        }}
                        options={categoryOptions}
                    />
                </div>
                <div className="ct-form-field">
                    <label htmlFor="ct-equip-unit">Equipment ID</label>
                    <Select
                        inputId="ct-equip-unit"
                        className="ct-form-select"
                        ariaLabel="Equipment ID"
                        placeholder="— Select Unit —"
                        value={selectedUnitId}
                        onChange={setSelectedUnitId}
                        options={filteredUnits.map((u: SingleUnit) => ({
                            value: u.id,
                            label: u.name,
                        }))}
                    />
                </div>
                <div className="ct-form-field">
                    <span className="ct-field-label" id="ct-equip-description-label">Description</span>
                    <p className="ct-equip-description" aria-labelledby="ct-equip-description-label">
                        {unitDescription || '—'}
                    </p>
                </div>
            </div>

            {/* ===== Service Details (Task 6.2) ===== */}
            <div className="ct-form-section">
                <h3 className="ct-form-section-title">Service Details</h3>
                <div className="ct-field-line">
                    <label htmlFor="ct-date-input">Date of Input</label>
                    <InputField
                        inputId="ct-date-input"
                        className="ct-form-input"
                        ariaLabel="Date of Input"
                        value={dateOfInput}
                        onChange={() => undefined}
                        disabled
                    />
                </div>
                <div className="ct-field-line">
                    <label htmlFor="ct-date-service">Date of Service</label>
                    <DateField
                        inputId="ct-date-service"
                        value={dateOfService}
                        onChange={setDateOfService}
                        ariaLabel="Date of Service"
                    />
                </div>
                <div className="ct-field-line">
                    <label htmlFor="ct-vendor">Vendor</label>
                    <SearchSuggest
                        inputId="ct-vendor"
                        className="ct-form-select"
                        ariaLabel="Vendor"
                        placeholder="Type a vendor name"
                        value={vendorId}
                        onChange={setVendorId}
                        options={vendorOptions}
                        clearable
                        actionOption={{
                            label: 'Create new vendor',
                            onClick: ({ searchText }) => {
                                setVendorDraftName(searchText.trim());
                                setShowVendorModal(true);
                            },
                        }}
                    />
                </div>
                <div className="ct-field-line">
                    <label htmlFor="ct-work-desc">Work Description</label>
                    <InputField
                        inputId="ct-work-desc"
                        className="ct-form-input"
                        ariaLabel="Work Description"
                        value={workDescription}
                        onChange={setWorkDescription}
                        placeholder="Describe the work performed"
                    />
                </div>
                <div className="ct-field-line">
                    <label htmlFor="ct-invoice">Invoice / PO / WO #</label>
                    <InputField
                        inputId="ct-invoice"
                        className="ct-form-input"
                        ariaLabel="Invoice / PO / WO #"
                        value={invoiceNumber}
                        onChange={setInvoiceNumber}
                        placeholder="Invoice number"
                    />
                </div>
            </div>


            {/* ===== Expense Categories (Task 6.4) ===== */}
            <div className="ct-form-section">
                <h3 className="ct-form-section-title">Expense Categories</h3>
                <p className="ct-field-helper">
                    These categories belong to this equipment type. Enter the amount on the category you want. A category added here is available the next time that equipment type is used.
                </p>
                {!selectedUnitId && (
                    <p className="ct-field-helper">Select a unit to enter category amounts.</p>
                )}

                {selectedUnitId && categoryLines.length > 0 && (
                    <div className="ct-category-list">
                        <div className="ct-category-header-row">
                            <span className="ct-category-col-name">Expense Category</span>
                            <span className="ct-category-col-balance">Starting Balance</span>
                            <span className="ct-category-col-cost">Cost</span>
                            <span className="ct-category-col-action"></span>
                        </div>

                        {categoryLines.map((line) => (
                            <div key={line.id} className="ct-category-line">
                                <span className="ct-category-col-name">
                                    <span className="ct-category-name-display">{line.categoryName}</span>
                                </span>

                                <span className="ct-category-col-balance">
                                    <span className="ct-col-caption">Starting Balance</span>
                                    {line.isNew && line.isEditing ? (
                                        <InputField
                                            type="number"
                                            className="ct-form-input ct-category-balance-input"
                                            ariaLabel="One-time starting balance"
                                            value={line.startingBalance ? String(line.startingBalance) : ''}
                                            onChange={(value) =>
                                                handleCategoryBalanceChange(line.id, parseFloat(value) || 0)
                                            }
                                            min={0}
                                            step={0.01}
                                            placeholder="0.00"
                                        />
                                    ) : (
                                        <span>{formatCurrency(line.startingBalance)}</span>
                                    )}
                                </span>

                                <span className="ct-category-col-cost">
                                    <span className="ct-col-caption">Cost</span>
                                    <InputField
                                        type="number"
                                        className="ct-form-input ct-category-cost-input"
                                        ariaLabel="Cost"
                                        value={line.cost ? String(line.cost) : ''}
                                        onChange={(value) =>
                                            handleCategoryCostChange(line.id, parseFloat(value) || 0)
                                        }
                                        min={0}
                                        step={0.01}
                                        placeholder="0.00"
                                    />
                                </span>

                                <span className="ct-category-col-action">
                                    <Btn
                                        variant="link"
                                        buttonClasses="ct-category-remove-btn"
                                        buttonText="— Remove"
                                        ariaLabel="Remove expense category"
                                        buttonOnClick={() => handleRemoveCategoryLine(line.id)}
                                    />
                                </span>
                            </div>
                        ))}
                    </div>
                )}

                {selectedUnitId && (
                    <SearchSuggest
                        className="ct-form-select ct-category-select"
                        ariaLabel="Add expense category"
                        placeholder="Type a category name"
                        options={scopedExpenseCategories.map((category) => ({
                            value: category.id,
                            label: category.name,
                        }))}
                        value=""
                        onChange={() => undefined}
                        allowNew
                        newLabel="Add category"
                        onCommitNew={(text) => {
                            const name = text.trim();
                            if (!name) return;
                            const existing = scopedExpenseCategories.find(
                                (category) => category.name.toLowerCase() === name.toLowerCase(),
                            );
                            if (existing) return;
                            setCategoryLines((prev) => {
                                if (prev.some((line) => line.categoryName.toLowerCase() === name.toLowerCase())) {
                                    return prev;
                                }
                                return [
                                    ...prev,
                                    {
                                        id: uuidv4(),
                                        categoryId: '',
                                        categoryName: name,
                                        startingBalance: 0,
                                        cost: 0,
                                        isNew: true,
                                        isEditing: true,
                                    },
                                ];
                            });
                        }}
                    />
                )}
            </div>


            <div className="ct-form-section">
                <h3 className="ct-form-section-title">Labor</h3>
                <p className="ct-field-helper">
                    In-house labor uses the levels and rates from Resources. A level with no dollar rate is kept as hours.
                </p>
                {laborOptions.length === 0 && (
                    <p className="ct-field-helper">No labor rate is set in Resources. Hours are still saved.</p>
                )}
                {laborLines.map((line) => {
                    const lineCost = line.hoursST * line.rateST + line.hoursOT * line.rateOT;
                    const lineLabel = lineCost > 0
                        ? formatCurrency(lineCost)
                        : (laborHoursLabel(line.hoursST, line.hoursOT) || formatCurrency(0));
                    return (
                        <div key={line.id} className="ct-labor-line">
                            <div>
                                <label htmlFor={`ct-labor-level-${line.id}`}>Labor level</label>
                                <Select
                                    inputId={`ct-labor-level-${line.id}`}
                                    className="ct-form-select"
                                    ariaLabel="Labor level"
                                    placeholder="Hours only"
                                    value={line.categoryId || 'hours'}
                                    onChange={(value) => {
                                        if (value === 'hours') {
                                            setLaborLines((prev) => prev.map((row) => row.id === line.id ? {
                                                ...row,
                                                categoryId: '',
                                                categoryName: '',
                                                rateST: 0,
                                                rateOT: 0,
                                            } : row));
                                            return;
                                        }
                                        const selected = laborOptions.find((option) => option.id === value);
                                        setLaborLines((prev) => prev.map((row) => row.id === line.id ? {
                                            ...row,
                                            categoryId: selected?.id || '',
                                            categoryName: selected?.name || '',
                                            rateST: selected?.rateST || 0,
                                            rateOT: selected?.rateOT || 0,
                                        } : row));
                                    }}
                                    options={[
                                        { value: 'hours', label: 'Hours only' },
                                        ...laborOptions.map((option) => ({
                                            value: option.id,
                                            label: option.rateST || option.rateOT
                                                ? `${option.name} — ST $${option.rateST.toFixed(2)} / OT $${option.rateOT.toFixed(2)}`
                                                : `${option.name} — hours only`,
                                        })),
                                    ]}
                                />
                            </div>
                            <div>
                                <label htmlFor={`ct-labor-st-${line.id}`}>ST Hours</label>
                                <InputField
                                    inputId={`ct-labor-st-${line.id}`}
                                    type="number"
                                    className="ct-form-input"
                                    ariaLabel="ST Hours"
                                    value={line.hoursST ? String(line.hoursST) : ''}
                                    min={0}
                                    step={0.25}
                                    placeholder="0"
                                    onChange={(value) => {
                                        const hoursST = parseFloat(value) || 0;
                                        setLaborLines((prev) => prev.map((row) => row.id === line.id ? { ...row, hoursST } : row));
                                    }}
                                />
                            </div>
                            <div>
                                <label htmlFor={`ct-labor-ot-${line.id}`}>OT Hours</label>
                                <InputField
                                    inputId={`ct-labor-ot-${line.id}`}
                                    type="number"
                                    className="ct-form-input"
                                    ariaLabel="OT Hours"
                                    value={line.hoursOT ? String(line.hoursOT) : ''}
                                    min={0}
                                    step={0.25}
                                    placeholder="0"
                                    onChange={(value) => {
                                        const hoursOT = parseFloat(value) || 0;
                                        setLaborLines((prev) => prev.map((row) => row.id === line.id ? { ...row, hoursOT } : row));
                                    }}
                                />
                            </div>
                            <div className="ct-labor-line-amount" aria-live="polite">
                                {lineLabel}
                            </div>
                            <Btn
                                variant="link"
                                buttonClasses="ct-category-remove-btn"
                                buttonText="— Remove"
                                ariaLabel="Remove labor level"
                                buttonOnClick={() => setLaborLines((prev) => prev.length <= 1 ? [blankLaborLine()] : prev.filter((row) => row.id !== line.id))}
                            />
                        </div>
                    );
                })}
                <Btn
                    variant="link"
                    buttonClasses="ct-add-category-btn"
                    buttonIcon={<IconPlus />}
                    buttonText="Add labor level"
                    buttonOnClick={() => setLaborLines((prev) => [...prev, blankLaborLine()])}
                />
                <p className="ct-labor-summary">Labor cost: {laborSummaryText}</p>
            </div>

            {/* ===== Attachments (Task 6.6) ===== */}
            <div className="ct-form-section">
                <h3 className="ct-form-section-title">Attachments</h3>
                <div className="ct-attachments-area">
                    <p className="ct-attachments-prompt">Attach any supporting documents.</p>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={ACCEPTED_FILE_TYPES}
                        multiple
                        onChange={handleFileSelect}
                        className="ct-file-input"
                        id="ct-file-upload"
                    />
                    <Btn
                        variant="secondary-outlined"
                        buttonShape="pill"
                        buttonClasses="ct-file-upload-btn"
                        buttonIcon={<IconUpload />}
                        buttonText="Choose file"
                        buttonOnClick={() => fileInputRef.current?.click()}
                        buttonAriaProps={{ 'aria-controls': 'ct-file-upload' }}
                    />
                    <span className="ct-file-hint">PDF, Image, Document</span>
                </div>

                {attachments.length > 0 && (
                    <ul className="ct-attachment-list">
                        {attachments.map((att) => (
                            <li key={att.id} className="ct-attachment-item">
                                <span className="ct-attachment-name">{att.name}</span>
                                <BtnIconOnly
                                    size="sm"
                                    variant="danger"
                                    icon={<IconClose />}
                                    ariaLabel={`Remove ${att.name}`}
                                    buttonClasses="ct-attachment-remove"
                                    onClick={() => handleRemoveAttachment(att.id)}
                                />
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* ===== Total Expense Summary (Task 6.7) ===== */}
            <Card variant="card-sm" className="ct-total-card">
                <div className="ct-total-heading">
                    <span>Total Expense</span>
                    <span className="ct-total-heading-amount">{formatCurrency(totalExpense)}</span>
                </div>
                <div className="ct-total-breakdown">
                    {categoryLines.map((line) => (
                        <div key={line.id} className="ct-total-row">
                            <span>{line.categoryName || 'Unnamed Category'}:</span>
                            <span>{formatCurrency(line.cost || 0)}</span>
                        </div>
                    ))}
                    <div className="ct-total-row">
                        <span>Labor:</span>
                        <span>{laborSummaryText}</span>
                    </div>
                    <div className="ct-total-row ct-total-row-grand">
                        <span>Total Cost:</span>
                        <span>{formatCurrency(totalExpense)}</span>
                    </div>
                </div>
            </Card>

            {/* ===== Action Buttons (Task 6.1) ===== */}
            <div className="ct-form-actions">
                {!isEditMode && (
                    <Btn
                        variant="secondary-outlined"
                        buttonShape="pill"
                        buttonIcon={<IconPlus />}
                        buttonText="Save & Add Another"
                        buttonDisabled={isSaving}
                        buttonOnClick={() => handleSave(true)}
                    />
                )}
                <Btn
                    variant="primary"
                    buttonShape="pill"
                    buttonText={isSaving ? 'Saving…' : 'Save Expense'}
                    buttonDisabled={isSaving}
                    buttonOnClick={() => handleSave(false)}
                />
                <Btn
                    variant="danger-outlined"
                    buttonShape="pill"
                    buttonIcon={<IconTrash />}
                    buttonText="Cancel"
                    buttonDisabled={isSaving}
                    buttonOnClick={onBack}
                />
            </div>

            {/* ===== Vendor Create Modal ===== */}
            <VendorModal
                key={vendorDraftName}
                isOpen={showVendorModal}
                onClose={() => {
                    setShowVendorModal(false);
                    setVendorDraftName('');
                }}
                onSave={handleVendorModalSave}
                editVendor={null}
                companyId={companyId}
                initialName={vendorDraftName}
            />
        </div>
    );
};
